import maxBy from "lodash.maxby";

import {
  CreateExclusionGroupEntityFe,
  ExclusionGroupDto,
  ExclusionGroupEntityDto,
  ExperienceListDto,
} from "@intelligems/ig-types";
import { ensureExclusionGroupEntityPercentage } from "@intelligems/ig-utils";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { GroupSliderRoundedPercentages } from "@src/store/slices/components/groupSlider/types";

import {
  initialExclusionGroupEntityStateFunc,
  initialExclusionGroupStateFunc,
} from "../../api/initials";
import { IExclusionGroupsState } from "../types/exclusionGroups";

const sliceInitialExclusionGroupsState: IExclusionGroupsState = {
  isSuccess: false,
  exclusionGroups: [],
  selectedExclusionGroup: undefined,
};

const exclusionGroupsSlice = rtk.createSlice({
  name: "exclusionGroups",
  initialState: sliceInitialExclusionGroupsState,
  reducers: {
    resetExclusionGroups: (draft) => {
      draft.isSuccess = false;
      draft.exclusionGroups = [];
      draft.selectedExclusionGroup = undefined;
    },
    initExclusionGroupsState: (
      draft,
      action: PayloadAction<{ exclusionGroups: ExclusionGroupDto[] }>
    ) => {
      draft.isSuccess = true;
      draft.exclusionGroups = action.payload.exclusionGroups;
    },
    initNewExclusionGroupState: (
      draft,
      action: PayloadAction<string | undefined>
    ) => {
      draft.selectedExclusionGroup = initialExclusionGroupStateFunc(
        action.payload
      );
    },
    initSelectedEntityExclusionGroupState: (
      draft,
      action: PayloadAction<{ selectedEntityId: string }>
    ) => {
      let hasExistingExclusionGroup = false;
      draft.exclusionGroups.forEach((exclusionGroup) => {
        if (
          exclusionGroup.exclusionGroupEntities.some(
            (value) => value.experienceId === action.payload.selectedEntityId
          )
        ) {
          draft.selectedExclusionGroup = exclusionGroup;
          hasExistingExclusionGroup = true;
        }
      });
      if (!hasExistingExclusionGroup) {
        draft.selectedExclusionGroup = undefined;
      }
      return draft;
    },
    exclusionGroupSlide: (
      draft,
      action: PayloadAction<GroupSliderRoundedPercentages>
    ) => {
      const entities = draft.selectedExclusionGroup?.exclusionGroupEntities;
      if (!draft.isSuccess || !entities) return draft;

      entities.forEach((entity, index) => {
        entity.percentage = action.payload[index] ?? 0;
      });
    },
    setSelectedExclusionGroup: (
      draft,
      action: PayloadAction<{ exclusionGroupId: string }>
    ) => {
      const index = draft.exclusionGroups.findIndex(
        (group) => group.id === action.payload.exclusionGroupId
      );
      if (index > -1) {
        draft.selectedExclusionGroup = draft.exclusionGroups[index];
      }
      return draft;
    },
    resetSelectedExclusionGroup: (draft) => {
      draft.selectedExclusionGroup = undefined;
    },
    setExclusionGroupName: (draft, action: PayloadAction<string>) => {
      if (draft.selectedExclusionGroup) {
        draft.selectedExclusionGroup.name = action.payload;
      }
      return draft;
    },
    setExclusionGroupWeightedValue: (
      draft,
      action: PayloadAction<{
        value: boolean;
        experiencesList: ExperienceListDto[];
      }>
    ) => {
      if (draft.selectedExclusionGroup) {
        draft.selectedExclusionGroup.hasWeightedValues = action.payload.value;
        if (!action.payload.value) {
          draft.selectedExclusionGroup.exclusionGroupEntities =
            ensureExclusionGroupEntityPercentage(
              draft.selectedExclusionGroup.exclusionGroupEntities,
              action.payload.experiencesList
            );
        }
      }
      return draft;
    },
    removeTestFromGroup: (
      draft,
      action: PayloadAction<{
        entityId: string;
        experiencesList: ExperienceListDto[];
      }>
    ) => {
      if (
        draft.selectedExclusionGroup &&
        draft.selectedExclusionGroup.exclusionGroupEntities
      ) {
        let tempExclusionGroupEntities =
          draft.selectedExclusionGroup.exclusionGroupEntities.filter(
            (_: CreateExclusionGroupEntityFe | ExclusionGroupEntityDto) =>
              _.experienceId !== action.payload.entityId
          );

        tempExclusionGroupEntities = ensureExclusionGroupEntityPercentage(
          tempExclusionGroupEntities,
          action.payload.experiencesList
        );
        draft.selectedExclusionGroup.exclusionGroupEntities =
          tempExclusionGroupEntities;
      }
      return draft;
    },
    addTestToExclusionGroup: (
      draft,
      action: PayloadAction<{
        entityId: string | undefined;
        experiencesList: ExperienceListDto[];
      }>
    ) => {
      if (
        draft.selectedExclusionGroup &&
        draft.selectedExclusionGroup.exclusionGroupEntities
      ) {
        let tempExclusionGroupEntities =
          draft.selectedExclusionGroup.exclusionGroupEntities;

        tempExclusionGroupEntities.push(
          // @ts-ignore
          initialExclusionGroupEntityStateFunc(action.payload.entityId)
        );

        tempExclusionGroupEntities = ensureExclusionGroupEntityPercentage(
          tempExclusionGroupEntities,
          action.payload.experiencesList
        );

        draft.selectedExclusionGroup.exclusionGroupEntities =
          tempExclusionGroupEntities;
      }
      return draft;
    },
    setExclusionGroupReassignOnEnd: (draft, action: PayloadAction<boolean>) => {
      if (draft.selectedExclusionGroup) {
        draft.selectedExclusionGroup.reassignOnEnd = action.payload;
      }
      return draft;
    },
    setAddExclusionGroupTests: (
      draft,
      action: PayloadAction<{
        entityIds: string[];
        experiencesList: ExperienceListDto[];
      }>
    ) => {
      if (draft.selectedExclusionGroup) {
        const formattedNewTests: CreateExclusionGroupEntityFe[] = [];

        const maxOrder =
          maxBy(draft.selectedExclusionGroup?.exclusionGroupEntities, "order")
            ?.order || 0;
        action.payload.entityIds.forEach((id, index) => {
          formattedNewTests.push(
            initialExclusionGroupEntityStateFunc(id, maxOrder + index + 1)
          );
        });

        draft.selectedExclusionGroup.exclusionGroupEntities =
          ensureExclusionGroupEntityPercentage(
            [
              ...draft.selectedExclusionGroup.exclusionGroupEntities,
              ...formattedNewTests,
            ],
            action.payload.experiencesList
          );
      }
      return draft;
    },
  },
  selectors: {
    allExclusionGroups: (state) => state.exclusionGroups,
    selectedExclusionGroup: (state) => state.selectedExclusionGroup,
    selectedStoredExclusionGroup: (state) =>
      state.exclusionGroups.find(
        (g) => g.id === state.selectedExclusionGroup?.id
      ),
  },
});

export const {
  initExclusionGroupsState,
  initSelectedEntityExclusionGroupState,
  setExclusionGroupName,
  initNewExclusionGroupState,
  setAddExclusionGroupTests,
  removeTestFromGroup,
  setSelectedExclusionGroup,
  addTestToExclusionGroup,
  setExclusionGroupReassignOnEnd,
  resetSelectedExclusionGroup,
  resetExclusionGroups,
  exclusionGroupSlide,
  setExclusionGroupWeightedValue,
} = exclusionGroupsSlice.actions;

export const exclusionGroupsReducer = exclusionGroupsSlice.reducer;
export const {
  allExclusionGroups,
  selectedExclusionGroup,
  selectedStoredExclusionGroup,
} = exclusionGroupsSlice.selectors;
